/**
 * ProjectsContent - Content renderer for Projects panel
 * 
 * Displays project cards with metadata and document counts.
 * Clicking a project opens its documents in the document viewer.
 */

import { useState } from 'react';
import type { ProjectInstance } from '../../types';
import { projectsApi } from '../../services/api';
import ConfirmationModal from '../ConfirmationModal';
import { 
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Label,
  Input,
} from '@/components/ui';
import { logger } from '../../utils/logger';

interface ProjectsContentProps {
  projects: ProjectInstance[];
  loading: boolean;
  isSearching: boolean;
  searchQuery: string;
  onProjectClick: (project: ProjectInstance) => void;
  onCreateProject: () => void;
  onRefreshProjects?: () => void;
}

export function ProjectsContent({
  projects,
  loading,
  isSearching,
  searchQuery,
  onProjectClick,
  onCreateProject,
  onRefreshProjects,
}: ProjectsContentProps) {
  
  // Clone modal state
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [projectToClone, setProjectToClone] = useState<ProjectInstance | null>(null);
  const [cloneName, setCloneName] = useState('');
  const [cloneCode, setCloneCode] = useState('');
  const [isCloning, setIsCloning] = useState(false);
  
  // Archive modal state
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [projectToArchive, setProjectToArchive] = useState<ProjectInstance | null>(null);
  
  // Clone handler
  const handleCloneProject = (project: ProjectInstance, e: React.MouseEvent) => {
    e.stopPropagation();
    setProjectToClone(project);
    setCloneName(`${project.name} (Copy)`);
    setCloneCode('');
    setShowCloneModal(true);
  };
  
  // Archive handler
  const handleArchiveProject = (project: ProjectInstance, e: React.MouseEvent) => {
    e.stopPropagation();
    setProjectToArchive(project);
    setShowArchiveModal(true);
  };
  
  // Confirm clone
  const confirmCloneProject = async () => {
    if (!projectToClone || !cloneName.trim()) return;
    
    setIsCloning(true);
    try {
      await projectsApi.create(cloneName.trim(), projectToClone.answers || {}, {
        projectCode: cloneCode || undefined,
        projectTitle: projectToClone.projectTitle ? `${projectToClone.projectTitle} (Copy)` : undefined,
        customer: projectToClone.customer || undefined,
        program: projectToClone.program || undefined,
        certificationAuthority: projectToClone.certificationAuthority || undefined,
        certificationBasis: projectToClone.certificationBasis || undefined
      });
      
      setShowCloneModal(false);
      setProjectToClone(null);
      setCloneName('');
      setCloneCode('');
      onRefreshProjects?.();
    } catch (err) {
      logger.error('Failed to clone project:', err);
    } finally {
      setIsCloning(false);
    }
  };
  
  // Confirm archive
  const confirmArchiveProject = async () => {
    if (!projectToArchive) return;
    
    await projectsApi.updateMetadata(projectToArchive.id, {
      projectTitle: `[ARCHIVED] ${projectToArchive.projectTitle || projectToArchive.name}`
    });
    
    setShowArchiveModal(false);
    setProjectToArchive(null);
    onRefreshProjects?.();
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3 mx-auto"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-28 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // Searching state
  if (isSearching) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin text-3xl mb-3">🔍</div>
        <p className="text-muted-foreground">Searching projects...</p>
      </div>
    );
  }
  
  // Filter projects by search query
  const filteredProjects = searchQuery.trim()
    ? projects.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.projectCode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.customer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.program?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : projects;
  
  // Empty state
  if (projects.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-5xl mb-4">📁</div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          No projects yet
        </h3>
        <p className="text-muted-foreground mb-4">
          Create your first certification project to get started
        </p>
        <Button onClick={onCreateProject}>
          + New Project
        </Button>
      </div>
    );
  }
  
  // No search results
  if (searchQuery.trim() && filteredProjects.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-4xl mb-3">📭</div>
        <p className="text-muted-foreground">No projects match "{searchQuery}"</p>
        <p className="text-sm text-muted-foreground mt-1">
          Try a different search term
        </p>
      </div>
    );
  }
  
  // Group projects by status/activity
  const activeProjects = filteredProjects.filter(p => 
    p.documents && p.documents.some(d => d.status === 'Draft' || d.status === 'In Review')
  );
  const otherProjects = filteredProjects.filter(p => 
    !activeProjects.includes(p)
  );
  
  return (
    <div className="p-4 space-y-6">
      {/* Header with count and create button */}
      <div className="flex items-center justify-between">
        {searchQuery.trim() ? (
          <div className="text-sm text-muted-foreground">
            Found {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">
            {projects.length} project{projects.length !== 1 ? 's' : ''}
          </div>
        )}
        <Button
          onClick={onCreateProject}
          size="sm"
        >
          + New Project
        </Button>
      </div>
      
      {/* Active Projects */}
      {activeProjects.length > 0 && (
        <ProjectGroup
          label="Active Projects"
          icon="🔄"
          projects={activeProjects}
          onProjectClick={onProjectClick}
          onCloneProject={handleCloneProject}
          onArchiveProject={handleArchiveProject}
          defaultExpanded={true}
        />
      )}
      
      {/* All/Other Projects */}
      <ProjectGroup
        label={activeProjects.length > 0 ? "Other Projects" : "All Projects"}
        icon="📁"
        projects={activeProjects.length > 0 ? otherProjects : filteredProjects}
        onProjectClick={onProjectClick}
        onCloneProject={handleCloneProject}
        onArchiveProject={handleArchiveProject}
        defaultExpanded={activeProjects.length === 0}
      />
      
      {/* Clone Project Modal */}
      <Dialog open={showCloneModal && !!projectToClone} onOpenChange={(open) => {
        if (!open) {
          setShowCloneModal(false);
          setProjectToClone(null);
          setCloneName('');
          setCloneCode('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Clone Project</DialogTitle>
            <DialogDescription>
              {projectToClone && `Create a copy of "${projectToClone.name}"`}
            </DialogDescription>
          </DialogHeader>
          {projectToClone && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="clone-name">New Project Name *</Label>
                <Input
                  id="clone-name"
                  value={cloneName}
                  onChange={(e) => setCloneName(e.target.value)}
                  placeholder="Enter project name"
                  autoFocus
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="clone-code">New Project Code (optional)</Label>
                <Input
                  id="clone-code"
                  value={cloneCode}
                  onChange={(e) => setCloneCode(e.target.value.toUpperCase())}
                  placeholder="e.g., CLONE1"
                  maxLength={8}
                />
              </div>
              
              <div className="p-3 bg-info-light border border-info rounded-lg text-sm">
                <p className="font-medium mb-1">What will be cloned:</p>
                <ul className="text-xs text-muted-foreground list-disc pl-4">
                  <li>Project settings and metadata</li>
                  <li>Customer and program information</li>
                  <li>Certification basis</li>
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => {
                setShowCloneModal(false);
                setProjectToClone(null);
                setCloneName('');
                setCloneCode('');
              }}
              variant="outline"
              disabled={isCloning}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmCloneProject}
              disabled={!cloneName.trim() || isCloning}
            >
              {isCloning ? 'Cloning...' : 'Clone Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Archive Project Modal */}
      {showArchiveModal && projectToArchive && (
        <ConfirmationModal
          title="Archive Project"
          message="Are you sure you want to archive this project? Archived projects will be marked with [ARCHIVED] prefix."
          confirmText="Archive Project"
          cancelText="Cancel"
          onConfirm={confirmArchiveProject}
          onCancel={() => {
            setShowArchiveModal(false);
            setProjectToArchive(null);
          }}
        >
          <div className="p-3 bg-muted/30 border border-border rounded-lg text-sm">
            <div className="mb-2">
              <strong>Project:</strong> {projectToArchive.name}
            </div>
            {projectToArchive.projectCode && (
              <div className="mb-2">
                <strong>Code:</strong> {projectToArchive.projectCode}
              </div>
            )}
            {projectToArchive.documents && projectToArchive.documents.length > 0 && (
              <div>
                <strong>Documents:</strong> {projectToArchive.documents.length}
              </div>
            )}
          </div>
        </ConfirmationModal>
      )}
    </div>
  );
}

// Project group component
interface ProjectGroupProps {
  label: string;
  icon: string;
  projects: ProjectInstance[];
  onProjectClick: (project: ProjectInstance) => void;
  onCloneProject: (project: ProjectInstance, e: React.MouseEvent) => void;
  onArchiveProject: (project: ProjectInstance, e: React.MouseEvent) => void;
  defaultExpanded: boolean;
}

function ProjectGroup({
  label,
  icon,
  projects,
  onProjectClick,
  onCloneProject,
  onArchiveProject,
  defaultExpanded,
}: ProjectGroupProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  if (projects.length === 0) return null;
  
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Group header */}
      <Button
        onClick={() => setIsExpanded(!isExpanded)}
        variant="ghost"
        className="w-full flex items-center justify-between px-4 py-2 bg-muted/30 h-auto rounded-none"
      >
        <div className="flex items-center gap-2">
          <span>{icon}</span>
          <span className="font-medium text-foreground">
            {label}
          </span>
          <span className="text-sm text-muted-foreground">
            ({projects.length})
          </span>
        </div>
        <span className="text-muted-foreground">
          {isExpanded ? '▼' : '▶'}
        </span>
      </Button>
      
      {/* Projects */}
      {isExpanded && (
        <div className="divide-y divide-gray-100 dark:divide-slate-700">
          {projects.map(project => (
            <ProjectCard
              key={project.id}
              project={project}
              onClick={() => onProjectClick(project)}
              onClone={(e) => onCloneProject(project, e)}
              onArchive={(e) => onArchiveProject(project, e)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Project card component
interface ProjectCardProps {
  project: ProjectInstance;
  onClick: () => void;
  onClone: (e: React.MouseEvent) => void;
  onArchive: (e: React.MouseEvent) => void;
}

function ProjectCard({ project, onClick, onClone, onArchive }: ProjectCardProps) {
  const docCount = project.documents?.length || 0;
  const draftCount = project.documents?.filter(d => d.status === 'Draft').length || 0;
  const releasedCount = project.documents?.filter(d => d.status === 'Released').length || 0;
  
  return (
    <div
      className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={onClick}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-foreground truncate">
              {project.name}
            </h4>
            {project.projectCode && (
              <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-info-light text-info rounded-full font-mono">
                {project.projectCode}
              </span>
            )}
          </div>
          {project.projectTitle && (
            <p className="text-sm text-muted-foreground truncate">
              {project.projectTitle}
            </p>
          )}
        </div>
        
        {/* Actions and document count */}
        <div className="flex-shrink-0 flex items-center gap-2">
          {/* Action buttons */}
          <Button
            onClick={onClone}
            variant="ghost"
            size="icon"
            className="w-7 h-7"
            title="Clone project"
          >
            <span className="text-sm">📋</span>
          </Button>
          <Button
            onClick={onArchive}
            variant="ghost"
            size="icon"
            className="w-7 h-7"
            title="Archive project"
          >
            <span className="text-sm">📦</span>
          </Button>
          
          {/* Document count badge */}
          {docCount > 0 && (
            <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded-full">
              {docCount} doc{docCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
      
      {/* Metadata row */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        {project.customer && (
          <span className="flex items-center gap-1">
            <span className="text-muted-foreground">Customer:</span>
            <span className="text-foreground">{project.customer}</span>
          </span>
        )}
        {project.program && (
          <span className="flex items-center gap-1">
            <span className="text-muted-foreground">Program:</span>
            <span className="text-foreground">{project.program}</span>
          </span>
        )}
        {project.certificationAuthority && (
          <span className="px-1.5 py-0.5 bg-success-light text-success rounded text-xs">
            {project.certificationAuthority}
          </span>
        )}
      </div>
      
      {/* Document status summary */}
      {docCount > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {draftCount > 0 && (
            <span className="text-xs px-2 py-0.5 bg-warning-light text-warning rounded">
              {draftCount} Draft
            </span>
          )}
          {releasedCount > 0 && (
            <span className="text-xs px-2 py-0.5 bg-success-light text-success rounded">
              {releasedCount} Released
            </span>
          )}
        </div>
      )}
      
      {/* Certification basis tags */}
      {project.certificationBasis && project.certificationBasis.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {project.certificationBasis.map(basis => (
            <span
              key={basis}
              className="text-xs px-2 py-0.5 bg-info-light text-info rounded"
            >
              {basis}
            </span>
          ))}
        </div>
      )}
      
      {/* Created date */}
      <div className="mt-3 text-xs text-muted-foreground">
        Created {formatRelativeTime(project.createdAt)}
      </div>
    </div>
  );
}

// Helper to format relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return date.toLocaleDateString();
}
